/**
 * Currency Default Decimal Places
 * 
 * Uses ISO-4217 currency list for default decimal places.
 * Falls back to 2 decimals if currency not found.
 */

import { getCurrencyByCode } from "./iso4217Currencies";

/**
 * Get default decimal places for a currency code
 * @param currencyCode Currency code (e.g., "USD", "AMD")
 * @returns Default decimal places (0 for no decimals, 2 for two decimals, etc.)
 */
export const getDefaultDecimalPlaces = (currencyCode: string): number => {
  const currency = getCurrencyByCode(currencyCode);
  return currency?.decimalPlaces ?? 2; // Default to 2 if not found
};

/**
 * Get decimal places for a currency from currencies array or default
 * @param currencyCode Currency code (e.g., "USD", "AMD")
 * @param currencies Array of Currency objects (may not include default currency)
 * @param defaultCurrency Default currency code
 * @returns Decimal places for the currency
 */
export const getCurrencyDecimalPlaces = (
  currencyCode: string,
  currencies: Array<{ code: string; decimalPlaces?: number }>,
  defaultCurrency: string
): number => {
  // If it's the default currency, check if it's in currencies array
  if (currencyCode === defaultCurrency) {
    const currency = currencies.find((c) => c.code === currencyCode);
    if (currency && currency.decimalPlaces !== undefined) {
      return currency.decimalPlaces;
    }
    // Default currency not in currencies array, use default
    return getDefaultDecimalPlaces(currencyCode);
  }
  
  // Check currencies array
  const currency = currencies.find((c) => c.code === currencyCode);
  if (currency && currency.decimalPlaces !== undefined) {
    return currency.decimalPlaces;
  }
  
  // Fallback to default
  return getDefaultDecimalPlaces(currencyCode);
};

/**
 * Validate if an amount string has the correct number of decimal places for a currency
 * @param amountString Amount as string (e.g., "323.300420", "1000", "1.00")
 * @param currencyCode Currency code (e.g., "USD", "AMD")
 * @param currencies Array of Currency objects
 * @param defaultCurrency Default currency code
 * @returns Object with isValid boolean and errorMessage string
 */
export const validateAmountDecimalPlaces = (
  amountString: string,
  currencyCode: string,
  currencies: Array<{ code: string; decimalPlaces?: number }>,
  defaultCurrency: string
): { isValid: boolean; errorMessage: string } => {
  // Empty string is valid (user is still typing)
  if (!amountString || amountString.trim() === "") {
    return { isValid: true, errorMessage: "" };
  }
  
  // Get allowed decimal places for this currency
  const allowedDecimalPlaces = getCurrencyDecimalPlaces(currencyCode, currencies, defaultCurrency);
  
  // Parse the amount to check decimal places
  const amount = parseFloat(amountString);
  
  // If not a valid number, don't validate decimal places (let other validation handle it)
  if (isNaN(amount)) {
    return { isValid: true, errorMessage: "" };
  }
  
  // Special case: "0" is always valid regardless of decimal places
  // This allows creating transactions with 0 amount even for currencies without decimal places
  if (amount === 0) {
    return { isValid: true, errorMessage: "" };
  }
  
  // Check if amount has decimal point
  if (!amountString.includes(".")) {
    // No decimal point - valid for any currency
    return { isValid: true, errorMessage: "" };
  }
  
  // Get decimal part
  const decimalPart = amountString.split(".")[1];
  if (!decimalPart) {
    // Just a decimal point with no digits - valid
    return { isValid: true, errorMessage: "" };
  }
  
  // Count decimal places
  const actualDecimalPlaces = decimalPart.length;
  
  // Check if exceeds allowed decimal places
  if (actualDecimalPlaces > allowedDecimalPlaces) {
    // Generate error message with exact decimal format from settings
    let errorMessage = "";
    if (allowedDecimalPlaces === 0) {
      errorMessage = "This currency does not support decimal places (e.g., 1000)";
    } else if (allowedDecimalPlaces === 1) {
      errorMessage = "This currency supports only 1 decimal place (format: .0, e.g., 1.0)";
    } else {
      // Show exact decimal format from settings: ".00", ".000", ".0000" based on decimal places
      const decimalFormat = "." + "0".repeat(allowedDecimalPlaces);
      errorMessage = `This currency supports only ${allowedDecimalPlaces} decimal places (format: ${decimalFormat}, e.g., 1${decimalFormat})`;
    }
    
    return { isValid: false, errorMessage };
  }
  
  return { isValid: true, errorMessage: "" };
};

/**
 * Format an amount string to match the currency's decimal places
 * @param amountString Amount as string (e.g., "3092.39230", "3092", "1.5")
 * @param currencyCode Currency code (e.g., "USD", "AMD")
 * @param currencies Array of Currency objects
 * @param defaultCurrency Default currency code
 * @returns Formatted amount string (e.g., "3092.39", "3092.00", "1.50")
 */
export const formatAmountToCurrencyDecimalPlaces = (
  amountString: string,
  currencyCode: string,
  currencies: Array<{ code: string; decimalPlaces?: number }>,
  defaultCurrency: string
): string => {
  // Empty string or invalid input - return as is
  if (!amountString || amountString.trim() === "") {
    return amountString;
  }
  
  // Parse the amount
  const amount = parseFloat(amountString);
  
  // If not a valid number, return as is
  if (isNaN(amount)) {
    return amountString;
  }
  
  // Get allowed decimal places for this currency
  const allowedDecimalPlaces = getCurrencyDecimalPlaces(currencyCode, currencies, defaultCurrency);
  
  // Format the amount to the correct decimal places
  // If 0 decimal places, round to integer
  if (allowedDecimalPlaces === 0) {
    return Math.round(amount).toString();
  }
  
  // Format with the required decimal places (always show decimals)
  return amount.toFixed(allowedDecimalPlaces);
};

/**
 * Convert a formatted amount string back to a raw value for editing
 * Removes trailing zeros after decimal point to allow natural editing
 * @param amountString Formatted amount string (e.g., "3092.00", "1.50", "82.00")
 * @returns Raw value string suitable for calculator (e.g., "3092", "1.5", "82")
 */
export const convertFormattedAmountToRawValue = (amountString: string): string => {
  if (!amountString || amountString.trim() === "") {
    return "";
  }
  
  // Trim the input
  let cleaned = amountString.trim();
  
  // First, remove trailing zeros after decimal point directly from the string
  // This handles cases like "33.00" -> "33", "82.00" -> "82" more reliably
  // Match patterns like ".00", ".000", ".0", etc. at the end
  cleaned = cleaned.replace(/\.0+$/, "");
  
  // If after removing .00 the string is empty or just a decimal point, return "0"
  if (cleaned === "" || cleaned === ".") {
    return "0";
  }
  
  // Parse the cleaned amount to ensure it's a valid number
  const amount = parseFloat(cleaned);
  
  // If not a valid number, return the original string
  if (isNaN(amount)) {
    return amountString;
  }
  
  // Convert to string - this will naturally remove unnecessary decimals
  // e.g., 82 -> "82", 1.5 -> "1.5", 33 -> "33"
  const str = amount.toString();
  
  // If the result still has a decimal point with trailing zeros, remove them
  // This handles edge cases where parseFloat might preserve some formatting
  if (str.includes(".")) {
    const withoutTrailingZeros = str.replace(/\.?0+$/, "");
    return withoutTrailingZeros || "0";
  }
  
  return str;
};
